<template>
    <modal
        ref="modal"
        class="component-dialogue"
        :class="[className]"
        :auto-hide="hideOnBackgroundClick"
        :with-close-button="withCloseButton"
        :close-button-icon="closeButtonIcon"
        @modalClosed="hide"
    >
        <template
            v-if="title"
            slot="header"
        >
            <div class="modal-header-title">
                <icon
                    v-if="!!titleIcon"
                    :class="titleIconStyles"
                    :for="titleIcon"
                />
                {{ title }}
            </div>
        </template>
        <div class="flex flex-col">
            <div
                v-if="message"
                class="text-center"
            >
                {{ message }}
            </div>
            <component
                :is="component"
                v-if="component"
                ref="component"
                v-bind="componentConfig"
                @hook:updated="onComponentUpdate"
                @hook:mounted="addEventListeners"
            >
                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-if="componentSlot"
                    v-html="componentSlot"
                />
                <!-- eslint-enable vue/no-v-html -->
            </component>
        </div>
        <div
            v-if="buttons && buttons.length"
            slot="footer"
        >
            <div class="flex flex-col items-center justify-center">
                <div class="flex items-center w-full justify-center">
                    <template
                        v-for="(activity, key) in buttonStatuses"
                    >
                        <component
                            :is="buttons[key].type"
                            v-if="buttons[key].type"
                            :key="key"
                            :class="[buttons[key].class]"
                            v-bind="buttons[key].props"
                            :disabled="activity"
                            @click="resolveClick(buttons[key])"
                        >
                            {{ buttons[key].translate ? $t(buttons[key].translate) : buttons[key].label }}
                        </component>
                        <button
                            v-else
                            :key="key"
                            :class="[buttons[key].class]"
                            :disabled="activity"
                            @click="resolveClick(buttons[key])"
                        >
                            {{ buttons[key].translate ? $t(buttons[key].translate) : buttons[key].label }}
                        </button>
                    </template>
                </div>
                <div
                    v-if="withCancel"
                    class="flex items-center mt-xxs"
                >
                    {{ $t('common.or') }}
                    <ui-text-button
                        class="ml-xxxs text-purple body-semibold"
                        @click="hide"
                    >
                        {{ $t(`common.cancel`) }}
                    </ui-text-button>
                </div>
            </div>
        </div>
    </modal>
</template>
<script>
import Modal from './Modal.vue';
import Icon from 'common/UIToolKit/Icon.vue';
import UiTextButton from 'ui/v1/controls/UiTextButton.vue';
import { isEqual, sortBy } from 'lodash';

export default {
    components: {
        Modal,
        Icon,
        UiTextButton,
    },
    props: {
        title: {
            type: String,
            default: null,
        },
        message: {
            type: String,
            default: '',
        },
        buttons: {
            type: Array,
            default: null,
        },
        component: {
            type: String,
            default: null,
        },
        componentConfig: {
            type: Object,
            default: null,
        },
        events: {
            type: Object,
            default: null,
        },
        hideOnBackgroundClick: {
            type: Boolean,
            default: true,
        },
        className: {
            type: String,
            default: null,
        },
        withCloseButton: {
            type: Boolean,
            default: false,
        },
        withCancel: {
            type: Boolean,
            default: false,
        },
        closeButtonIcon: {
            type: String,
            default: 'close-c',
        },
        titleIcon: {
            type: String,
            default: null,
        },
        titleIconStyles: {
            type: String,
            default: null,
        },
        componentSlot: {
            type: null,
            default: null,
        },
    },
    data() {
        return {
            resolve: null,
            reject: null,
            buttonStatuses: [],
        };
    },
    watch: {
        buttons() {
            this.updateButtonStatuses();
        },
    },

    mounted() {
        this.updateButtonStatuses();
    },

    methods: {
        onComponentUpdate() {
            this.updateButtonStatuses();
        },
        show() {
            this.promise = new Promise((resolve, reject) => {
                this.resolve = resolve || true;
                this.reject = reject || false;

                this.$refs.modal.show();
            });

            return this.promise;
        },
        resolveClick(button) {
            this.resolve({
                value: button.value,
                modal: this,
                component: this.$refs.component,
            });
        },
        updateButtonStatuses() {
            if (!this.buttons) {
                return;
            }

            const statuses = [];
            this.buttons.map((button, i) => {
                statuses[i] = (this.$refs.component && button.disable)
                    ? button.disable(this.$refs.component)
                    : false;
            });
            if (!isEqual(sortBy(statuses), sortBy(this.buttonStatuses))) {
                this.buttonStatuses = statuses;
            }
        },
        hide() {
            if (this.$refs.modal) {
                this.$refs.modal.hide();
            }
            this.reject({
                value: false,
                modal: this,
                component: this.$refs.component,
            });
        },
        addEventListeners() {
            if (!this.events) {
                return;
            }
            Object.keys(this.events).map((key) => {
                this.$refs.component.$on(key, (...args) => {
                    this.events[key]({
                        modal: this,
                        component: this.$refs.component,
                    }, args);
                });
            });
        },
    },
};
</script>
<style lang="scss">
    @import "~common/scss/variables";

    .component-dialogue {
        .modal-footer > div {
            display: flex;
        }
    }
</style>
